import {
  Bone,
  HeaderSkeleton,
  placeholderKeys,
  SkeletonRegion,
} from '#ui/patterns/skeleton-blocks.tsx'

/**
 * What a page looks like while its loader is still running.
 *
 * The console had nothing here. `defaultPendingMs: 300` was set and no `pendingComponent` existed
 * anywhere, so a slow navigation left the *previous* page on screen, fully interactive, for as long
 * as the next one took — which is the worst of the options, because it invites somebody to click
 * something on a page that is already going away.
 *
 * **It is shaped like a page rather than a spinner.** A heading block and a few rows, at roughly the
 * proportions every screen in this console actually has: a title, a line of context, then a table.
 * A centered spinner says "wait"; this says "wait, and here is roughly what is coming", which is the
 * difference between a pause and a jolt when the content lands.
 *
 * **`aria-busy` and one live announcement, not a label per box.** Fourteen skeleton rectangles each
 * announcing themselves is fourteen announcements of nothing. The region says it is busy once, and
 * the individual shapes are hidden.
 *
 * It appears only after `defaultPendingMs` (300ms), so a navigation that resolves from cache — most
 * of them, since the router preloads on intent — never shows it at all. A skeleton that flashes on
 * every fast navigation is worse than none.
 */
export function PageSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton />
      <div className="flex flex-col gap-3">
        {placeholderKeys('row', rows).map((key) => (
          <Bone key={key} className="h-12 w-full" />
        ))}
      </div>
    </SkeletonRegion>
  )
}
